// Supabase-based multiplayer service implementation
import { databaseService } from './databaseService';
import { RealtimeChannel, RealtimePresenceState } from '@supabase/supabase-js';
import type { UserProfile, GameSession, MultiplayerRoom, PlayerChoice, StorySegment, ChatMessage } from '../types';

export interface RoomState {
  currentStorySegment: StorySegment | null;
  companions: CompanionInfo[];
  activePlayers: string[];
  chatMessages: ChatMessage[];
  recentChoices: RoomChoice[];
}

export interface CompanionInfo {
  id: string;
  name: string;
  personality: string;
  voice: 'male' | 'female';
  isNPC: boolean;
  userId?: string; // Only for real players
}

export interface RoomChoice {
  userId: string;
  userName: string;
  choice: string;
  timestamp: Date;
  affectsStory: boolean;
}

export interface EnhancedPlayerProfile extends UserProfile {
  luck: number; // 0-100, affects random events
  pendingDamage: PendingDamage[];
  currentHP: number;
  maxHP: number;
  reputation: number;
  crossGameStats: CrossGameStats;
}

export interface PendingDamage {
  id: string;
  fromPlayerId: string;
  amount: number;
  damageType: 'physical' | 'psychic' | 'environmental';
  timestamp: Date;
  resolveOnNextChoice: boolean;
}

export interface CrossGameStats {
  totalGamesPlayed: number;
  averageLuck: number;
  multiplayerWins: number;
  nftCollected: number;
  universalReputation: number;
}

export interface NPCState {
  id: string;
  name: string;
  personality: NPCPersonality;
  status: 'hostile' | 'friendly' | 'neutral' | 'trader' | 'ally';
  location: string;
  recentActivity: string[];
  messageThreshold: number; // 0-1, probability of sending message
  relationshipWithPlayers: Record<string, number>; // playerId -> relationship score
}

export interface NPCPersonality {
  archetype: 'warrior' | 'merchant' | 'scholar' | 'trickster' | 'oracle';
  chatFrequency: number; // 0-1
  aggressiveness: number; // 0-1
  helpfulness: number; // 0-1
  curiosity: number; // 0-1
}

export interface BranchingTimeline {
  branchId: string;
  parentBranchId?: string;
  playerId: string;
  choice: string;
  timestamp: Date;
  divergencePoint: string;
  mergeConditions: MergeCondition[];
  reality: 'primary' | 'alternate' | 'merged';
}

export interface MergeCondition {
  type: 'time_elapsed' | 'player_count' | 'story_event' | 'damage_resolved';
  value: any;
  fulfilled: boolean;
}

export interface BayesianLootEvent {
  eventId: string;
  playerId: string;
  baseChance: number;
  luckModifier: number;
  contextModifiers: Record<string, number>;
  finalProbability: number;
  outcome: 'success' | 'failure';
  lootGenerated?: NFTItem;
}

export interface NFTItem {
  id: string;
  name: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary' | 'quantum';
  properties: Record<string, any>;
  universalId: string; // Cross-game identifier
  quantumSignature?: string; // For quantum RNG integration
}

class MultiplayerStoryService {
  private roomChannels: Map<string, RealtimeChannel> = new Map();
  private roomListeners: Map<string, () => void> = new Map();
  private reconnectTimers: Map<string, NodeJS.Timeout> = new Map();
  private reconnectAttempts: Map<string, number> = new Map();
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000; // Start with 2 seconds
  private npcs: Map<string, NPCState> = new Map();
  private timelines: Map<string, BranchingTimeline> = new Map();
  private pendingMerges: Map<string, BranchingTimeline[]> = new Map();
  private supabase = databaseService.getSupabase();

  constructor() {
    this.initializeDefaultNPCs();
  }

  private initializeDefaultNPCs(): void {
    const defaultNPCs: NPCState[] = [
      {
        id: 'jax',
        name: 'JAX',
        personality: {
          archetype: 'warrior',
          chatFrequency: 0.7,
          aggressiveness: 0.8,
          helpfulness: 0.6,
          curiosity: 0.4
        },
        status: 'ally',
        location: 'cyberpunk_street',
        recentActivity: [],
        messageThreshold: 0.3,
        relationshipWithPlayers: {}
      },
      {
        id: 'lyra',
        name: 'Lyra',
        personality: {
          archetype: 'oracle',
          chatFrequency: 0.5,
          aggressiveness: 0.2,
          helpfulness: 0.9,
          curiosity: 0.8
        },
        status: 'friendly',
        location: 'neural_nexus',
        recentActivity: [],
        messageThreshold: 0.4,
        relationshipWithPlayers: {}
      }
    ];

    defaultNPCs.forEach(npc => this.npcs.set(npc.id, npc));
  }

  async createRoom(cartridgeId: string, hostProfile: UserProfile, settings: {
    isPublic?: boolean;
    maxPlayers?: number;
    roomName?: string;
  } = {}): Promise<{ roomId: string; shareableLink: string }> {
    try {
      console.log('[MultiplayerService] Creating room with settings:', settings);
      
      const room = await databaseService.createMultiplayerRoom({
        name: settings.roomName || `${hostProfile.name}'s Game`,
        description: `Multiplayer session for ${cartridgeId}`,
        maxPlayers: settings.maxPlayers || 4,
        is_public: settings.isPublic !== false,
        cartridgeId
      });

      console.log('[MultiplayerService] Room created successfully:', room.id);
      
      return {
        roomId: room.id,
        shareableLink: `${window.location.origin}?invite=${room.invite_code || room.id}`
      };
    } catch (error) {
      console.error('[MultiplayerService] Error creating room:', error);
      throw error;
    }
  }

  async joinRoom(roomId: string, userId: string): Promise<boolean> {
    try {
      console.log('[MultiplayerService] Joining room:', roomId);
      await databaseService.joinMultiplayerRoom(roomId, userId);
      console.log('[MultiplayerService] Successfully joined room');
      return true;
    } catch (error) {
      console.error('[MultiplayerService] Error joining room:', error);
      return false;
    }
  }

  async getRoomState(roomId: string): Promise<RoomState | null> {
    try {
      // For now, return a basic room state
      // This would be enhanced to include actual story state, companions, etc.
      return {
        currentStorySegment: null,
        companions: [
          {
            id: 'jax',
            name: 'JAX',
            personality: 'Cynical hacker with a dry sense of humor',
            voice: 'male',
            isNPC: true
          },
          {
            id: 'lyra',
            name: 'Lyra',
            personality: 'Empathetic navigator who sees patterns in chaos',
            voice: 'female',
            isNPC: true
          }
        ],
        activePlayers: [],
        chatMessages: [],
        recentChoices: []
      };
    } catch (error) {
      console.error('[MultiplayerService] Error getting room state:', error);
      return null;
    }
  }

  async sendChatMessage(roomId: string, message: string, isVoice = false): Promise<void> {
    try {
      console.log('[MultiplayerService] Sending chat message to room:', roomId, message);
      
      const channel = this.roomChannels.get(roomId);
      if (!channel) {
        throw new Error('Not connected to room');
      }

      // Get current user
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user profile for display name
      const userProfile = await databaseService.getUserProfile(user.id);
      
      // Broadcast chat message
      await channel.send({
        type: 'broadcast',
        event: 'chat_message',
        payload: {
          id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          userId: user.id,
          userName: userProfile?.name || 'Anonymous',
          message,
          isVoice,
          timestamp: new Date().toISOString()
        }
      });

      // Also persist to database for history
      await this.supabase
        .from('chat_messages')
        .insert({
          room_id: roomId,
          user_id: user.id,
          user_name: userProfile?.name || 'Anonymous',
          message,
          is_voice: isVoice,
          created_at: new Date().toISOString()
        });
        
    } catch (error) {
      console.error('[MultiplayerService] Error sending chat message:', error);
      throw error;
    }
  }

  async makeStoryChoice(roomId: string, choice: string): Promise<void> {
    try {
      console.log('[MultiplayerService] Making story choice in room:', roomId, choice);
      
      const channel = this.roomChannels.get(roomId);
      if (!channel) {
        throw new Error('Not connected to room');
      }

      // Get current user
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user profile
      const userProfile = await databaseService.getUserProfile(user.id);

      // Create player choice
      const playerChoice: PlayerChoice = {
        id: `choice-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        playerId: user.id,
        choice,
        timestamp: new Date(),
        storySegmentId: `segment-${Date.now()}`, // Should be provided by game state
        influenceWeight: 1.0
      };

      // Broadcast the choice
      await channel.send({
        type: 'broadcast',
        event: 'player_choice',
        payload: {
          userId: user.id,
          userName: userProfile?.name || 'Anonymous',
          choice,
          timestamp: playerChoice.timestamp.toISOString(),
          affectsStory: true
        }
      });

      // Save to database
      await this.supabase
        .from('player_choices')
        .insert({
          room_id: roomId,
          player_id: playerChoice.playerId,
          choice: playerChoice.choice,
          story_segment_id: playerChoice.storySegmentId,
          influence_weight: playerChoice.influenceWeight,
          created_at: playerChoice.timestamp.toISOString()
        });

      // Update room state with the choice
      const { data: room } = await this.supabase
        .from('multiplayer_rooms')
        .select('game_state')
        .eq('id', roomId)
        .single();

      if (room) {
        const gameState = room.game_state || {};
        const recentChoices = gameState.recentChoices || [];
        
        // Add new choice and keep last 10
        recentChoices.unshift({
          userId: user.id,
          userName: userProfile?.name || 'Anonymous',
          choice,
          timestamp: playerChoice.timestamp.toISOString(),
          affectsStory: true
        });
        
        gameState.recentChoices = recentChoices.slice(0, 10);
        
        await this.supabase
          .from('multiplayer_rooms')
          .update({ game_state: gameState })
          .eq('id', roomId);
      }
      
    } catch (error) {
      console.error('[MultiplayerService] Error making story choice:', error);
      throw error;
    }
  }

  async addCompanionMessage(roomId: string, companionName: string, message: string): Promise<void> {
    try {
      console.log('[MultiplayerService] Adding companion message to room:', roomId, companionName, message);
      
      const channel = this.roomChannels.get(roomId);
      if (!channel) {
        throw new Error('Not connected to room');
      }

      // Broadcast companion message
      await channel.send({
        type: 'broadcast',
        event: 'companion_message',
        payload: {
          companionName,
          message,
          timestamp: new Date().toISOString()
        }
      });
      
    } catch (error) {
      console.error('[MultiplayerService] Error adding companion message:', error);
      throw error;
    }
  }

  subscribeToRoom(
    roomId: string, 
    onUpdate: (update: any) => void,
    onPlayerJoin?: (playerId: string) => void,
    onPlayerLeave?: (playerId: string) => void,
    onError?: (error: Error) => void
  ): () => void {
    console.log('[MultiplayerService] Subscribing to room:', roomId);
    
    try {
      // Clean up existing channel if any
      this.cleanupRoomChannel(roomId);

      // Create new channel with proper configuration
      const channel = this.supabase.channel(`room:${roomId}`, {
        config: {
          presence: {
            key: roomId
          },
          broadcast: {
            self: true, // Receive own broadcasts
            ack: true   // Wait for acknowledgment
          }
        }
      });

      // Store channel reference
      this.roomChannels.set(roomId, channel);

      // Subscribe to database changes
      channel
        .on('postgres_changes', 
          { 
            event: '*', 
            schema: 'public', 
            table: 'multiplayer_rooms',
            filter: `id=eq.${roomId}`
          }, 
          async (payload) => {
            console.log('[MultiplayerService] Room update received:', payload);
            
            if (payload.new) {
              const roomData = payload.new as any;
              
              // Transform database data to RoomState
              const roomState: RoomState = {
                currentStorySegment: roomData.game_state?.currentSegment || null,
                companions: this.getCompanionsForRoom(roomId),
                activePlayers: roomData.current_players || [],
                chatMessages: [], // Will be populated by chat subscription
                recentChoices: roomData.game_state?.recentChoices || []
              };
              
              // Notify about room updates
              onUpdate({
                roomState,
                gameState: roomData.game_state,
                storyProgression: roomData.game_state?.currentSegment
              });
            }
          }
        );

      // Subscribe to broadcast events
      channel
        .on('broadcast', { event: 'player_choice' }, (payload) => {
          console.log('[MultiplayerService] Player choice received:', payload);
          onUpdate({ playerChoice: payload.payload });
        })
        .on('broadcast', { event: 'chat_message' }, (payload) => {
          console.log('[MultiplayerService] Chat message received:', payload);
          onUpdate({ chatMessage: payload.payload });
        })
        .on('broadcast', { event: 'companion_message' }, (payload) => {
          console.log('[MultiplayerService] Companion message received:', payload);
          onUpdate({ companionMessage: payload.payload });
        })
        .on('broadcast', { event: 'story_update' }, (payload) => {
          console.log('[MultiplayerService] Story update received:', payload);
          onUpdate({ storyUpdate: payload.payload });
        });

      // Subscribe to presence (player join/leave)
      channel
        .on('presence', { event: 'sync' }, () => {
          const state = channel.presenceState();
          console.log('[MultiplayerService] Presence sync:', state);
          this.handlePresenceSync(state, roomId, onPlayerJoin, onPlayerLeave);
        })
        .on('presence', { event: 'join' }, ({ key, newPresences }) => {
          console.log('[MultiplayerService] Player joined:', key, newPresences);
          if (onPlayerJoin && newPresences.length > 0) {
            onPlayerJoin(newPresences[0].user_id);
          }
        })
        .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
          console.log('[MultiplayerService] Player left:', key, leftPresences);
          if (onPlayerLeave && leftPresences.length > 0) {
            onPlayerLeave(leftPresences[0].user_id);
          }
        });

      // Subscribe to the channel with error handling
      channel.subscribe(async (status, error) => {
        console.log('[MultiplayerService] Channel subscription status:', status, error);
        
        if (status === 'SUBSCRIBED') {
          console.log('[MultiplayerService] Successfully subscribed to room:', roomId);
          
          // Reset reconnection attempts on successful connection
          this.reconnectAttempts.set(roomId, 0);
          this.clearReconnectTimer(roomId);
          
          // Track presence for current user
          try {
            const { data: { user } } = await this.supabase.auth.getUser();
            if (user) {
              const presenceState = {
                user_id: user.id,
                online_at: new Date().toISOString()
              };
              console.log('[MultiplayerService] Tracking presence:', presenceState);
              
              await channel.track(presenceState).catch(err => {
                console.error('[MultiplayerService] Error tracking presence:', err);
              });
            } else {
              console.warn('[MultiplayerService] No authenticated user for presence tracking');
            }
          } catch (err) {
            console.error('[MultiplayerService] Error in presence setup:', err);
          }
          
          // Get initial room state
          try {
            const initialState = await this.getRoomState(roomId);
            if (initialState) {
              onUpdate({ roomState: initialState });
            }
          } catch (err) {
            console.error('[MultiplayerService] Error getting initial room state:', err);
          }
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error('[MultiplayerService] Channel subscription failed:', status, error);
          const errorMessage = error?.message || `Channel subscription failed: ${status}`;
          
          // Attempt reconnection
          this.scheduleReconnection(roomId, onUpdate, onPlayerJoin, onPlayerLeave, onError);
          
          onError?.(new Error(errorMessage));
        } else if (status === 'CLOSED') {
          console.log('[MultiplayerService] Channel closed for room:', roomId);
          
          // Attempt reconnection if not manually closed
          if (this.roomChannels.has(roomId)) {
            this.scheduleReconnection(roomId, onUpdate, onPlayerJoin, onPlayerLeave, onError);
          }
        }
      });

      // Return unsubscribe function
      return () => {
        console.log('[MultiplayerService] Unsubscribing from room:', roomId);
        this.cleanupRoomChannel(roomId);
      };
      
    } catch (error) {
      console.error('[MultiplayerService] Error subscribing to room:', error);
      onError?.(error as Error);
      return () => {};
    }
  }

  subscribeToChat(roomId: string, callback: (messages: ChatMessage[]) => void): () => void {
    console.log('[MultiplayerService] Subscribing to chat:', roomId);
    
    const channel = this.supabase.channel(`chat:${roomId}`);
    
    // Subscribe to chat message inserts
    channel
      .on('postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `room_id=eq.${roomId}`
        },
        async (payload) => {
          console.log('[MultiplayerService] New chat message:', payload);
          
          // Fetch all recent messages
          const { data: messages } = await this.supabase
            .from('chat_messages')
            .select('*')
            .eq('room_id', roomId)
            .order('created_at', { ascending: false })
            .limit(50);
          
          if (messages) {
            // Convert to ChatMessage type and reverse for chronological order
            const chatMessages: ChatMessage[] = messages.reverse().map(msg => ({
              id: msg.id,
              userId: msg.user_id,
              userName: msg.user_name,
              message: msg.message,
              timestamp: new Date(msg.created_at),
              isVoice: msg.is_voice || false
            }));
            
            callback(chatMessages);
          }
        }
      )
      .subscribe();

    // Get initial messages
    this.supabase
      .from('chat_messages')
      .select('*')
      .eq('room_id', roomId)
      .order('created_at', { ascending: false })
      .limit(50)
      .then(({ data: messages }) => {
        if (messages) {
          const chatMessages: ChatMessage[] = messages.reverse().map(msg => ({
            id: msg.id,
            userId: msg.user_id,
            userName: msg.user_name,
            message: msg.message,
            timestamp: new Date(msg.created_at),
            isVoice: msg.is_voice || false
          }));
          
          callback(chatMessages);
        }
      });

    return () => {
      console.log('[MultiplayerService] Unsubscribing from chat:', roomId);
      channel.unsubscribe();
    };
  }

  private cleanupRoomChannel(roomId: string): void {
    const channel = this.roomChannels.get(roomId);
    if (channel) {
      channel.unsubscribe();
      this.roomChannels.delete(roomId);
    }
    
    // Clear reconnection state
    this.clearReconnectTimer(roomId);
    this.reconnectAttempts.delete(roomId);
  }

  private handlePresenceSync(
    state: RealtimePresenceState<any>,
    roomId: string,
    onPlayerJoin?: (playerId: string) => void,
    onPlayerLeave?: (playerId: string) => void
  ): void {
    // Track active players from presence state
    const activePlayers = new Set<string>();
    
    Object.values(state).forEach((presences: any[]) => {
      presences.forEach(presence => {
        if (presence.user_id) {
          activePlayers.add(presence.user_id);
        }
      });
    });

    // Update room's current_players in database
    this.supabase
      .from('multiplayer_rooms')
      .update({ current_players: Array.from(activePlayers) })
      .eq('id', roomId)
      .then(({ error }) => {
        if (error) {
          console.error('[MultiplayerService] Error updating current players:', error);
        }
      });
  }

  private getCompanionsForRoom(roomId: string): CompanionInfo[] {
    // Return default companions for now
    // In a full implementation, this would be based on room/game state
    return [
      {
        id: 'jax',
        name: 'JAX',
        personality: 'Cynical hacker with a dry sense of humor',
        voice: 'male',
        isNPC: true
      },
      {
        id: 'lyra',
        name: 'Lyra',
        personality: 'Empathetic navigator who sees patterns in chaos',
        voice: 'female',
        isNPC: true
      }
    ];
  }

  async getAvailableRooms(cartridgeId?: string): Promise<MultiplayerRoom[]> {
    try {
      console.log('[MultiplayerService] Getting available rooms for cartridge:', cartridgeId);
      const rooms = await databaseService.getMultiplayerRooms(true); // Get public rooms
      
      // Filter by cartridge if specified
      if (cartridgeId) {
        return rooms.filter(room => room.cartridgeId === cartridgeId);
      }
      
      return rooms;
    } catch (error) {
      console.error('[MultiplayerService] Error getting available rooms:', error);
      return [];
    }
  }

  async updateRoomSettings(roomId: string, settings: {
    isPublic?: boolean;
    maxPlayers?: number;
    roomName?: string;
  }): Promise<void> {
    try {
      console.log('[MultiplayerService] Updating room settings:', roomId, settings);
      
      const updates: any = {};
      if (settings.isPublic !== undefined) updates.is_public = settings.isPublic;
      if (settings.maxPlayers !== undefined) updates.max_players = settings.maxPlayers;
      if (settings.roomName !== undefined) updates.name = settings.roomName;
      
      const { error } = await this.supabase
        .from('multiplayer_rooms')
        .update(updates)
        .eq('id', roomId);
        
      if (error) throw error;
      
    } catch (error) {
      console.error('[MultiplayerService] Error updating room settings:', error);
      throw error;
    }
  }

  async broadcastStoryUpdate(roomId: string, storySegment: StorySegment): Promise<void> {
    try {
      console.log('[MultiplayerService] Broadcasting story update to room:', roomId);
      
      const channel = this.roomChannels.get(roomId);
      if (!channel) {
        throw new Error('Not connected to room');
      }

      // Broadcast story update
      await channel.send({
        type: 'broadcast',
        event: 'story_update',
        payload: {
          narrative: storySegment.narrative,
          choices: storySegment.choices,
          imagePrompt: storySegment.imagePrompt,
          companionMessages: storySegment.companionMessages,
          timestamp: new Date().toISOString()
        }
      });

      // Also update room state in database
      const { error } = await this.supabase
        .from('multiplayer_rooms')
        .update({
          game_state: {
            currentSegment: storySegment,
            lastUpdate: new Date().toISOString()
          }
        })
        .eq('id', roomId);
        
      if (error) throw error;
      
    } catch (error) {
      console.error('[MultiplayerService] Error broadcasting story update:', error);
      throw error;
    }
  }

  isConnectedToRoom(roomId: string): boolean {
    return this.roomChannels.has(roomId);
  }

  async getCurrentRoomPlayers(roomId: string): Promise<string[]> {
    try {
      const { data, error } = await this.supabase
        .from('multiplayer_rooms')
        .select('current_players')
        .eq('id', roomId)
        .single();
        
      if (error) throw error;
      return data?.current_players || [];
      
    } catch (error) {
      console.error('[MultiplayerService] Error getting current players:', error);
      return [];
    }
  }

  private scheduleReconnection(
    roomId: string,
    onUpdate: (update: any) => void,
    onPlayerJoin?: (playerId: string) => void,
    onPlayerLeave?: (playerId: string) => void,
    onError?: (error: Error) => void
  ): void {
    const attempts = this.reconnectAttempts.get(roomId) || 0;
    
    if (attempts >= this.maxReconnectAttempts) {
      console.error('[MultiplayerService] Max reconnection attempts reached for room:', roomId);
      onError?.(new Error('Maximum reconnection attempts exceeded'));
      this.cleanupRoomChannel(roomId);
      return;
    }
    
    const delay = this.reconnectDelay * Math.pow(2, attempts); // Exponential backoff
    console.log(`[MultiplayerService] Scheduling reconnection attempt ${attempts + 1} for room ${roomId} in ${delay}ms`);
    
    const timer = setTimeout(() => {
      console.log('[MultiplayerService] Attempting to reconnect to room:', roomId);
      this.reconnectAttempts.set(roomId, attempts + 1);
      
      // Clean up existing channel
      this.cleanupRoomChannel(roomId);
      
      // Resubscribe
      this.subscribeToRoom(roomId, onUpdate, onPlayerJoin, onPlayerLeave, onError);
    }, delay);
    
    this.reconnectTimers.set(roomId, timer);
  }

  private clearReconnectTimer(roomId: string): void {
    const timer = this.reconnectTimers.get(roomId);
    if (timer) {
      clearTimeout(timer);
      this.reconnectTimers.delete(roomId);
    }
  }

  cleanup(): void {
    // Clean up all reconnect timers
    this.reconnectTimers.forEach((timer) => clearTimeout(timer));
    this.reconnectTimers.clear();
    this.reconnectAttempts.clear();
    
    // Clean up all room channels
    this.roomChannels.forEach((channel, roomId) => {
      console.log('[MultiplayerService] Cleaning up channel for room:', roomId);
      channel.unsubscribe();
    });
    this.roomChannels.clear();
    
    // Clean up any other subscriptions
    this.roomListeners.forEach(unsubscribe => unsubscribe());
    this.roomListeners.clear();
  }

  // Keep the advanced features that don't depend on Firebase
  async shouldNPCSendMessage(
    npcId: string, 
    gameContext: GameSession, 
    recentPlayerActions: string[]
  ): Promise<boolean> {
    const npc = this.npcs.get(npcId);
    if (!npc) return false;

    // Base probability from NPC personality
    let probability = npc.personality.chatFrequency;

    // Adjust based on recent actions
    for (const action of recentPlayerActions) {
      if (this.isActionRelevantToNPC(action, npc)) {
        probability += 0.2; // Increase probability for relevant actions
      }
    }

    // Random threshold check
    return Math.random() < Math.min(probability, 0.9);
  }

  private isActionRelevantToNPC(action: string, npc: NPCState): boolean {
    const actionLower = action.toLowerCase();
    
    // Different NPCs react to different action types
    switch (npc.personality.archetype) {
      case 'warrior':
        return actionLower.includes('fight') || actionLower.includes('attack') || actionLower.includes('defend');
      case 'merchant':
        return actionLower.includes('trade') || actionLower.includes('buy') || actionLower.includes('sell');
      case 'scholar':
        return actionLower.includes('study') || actionLower.includes('research') || actionLower.includes('analyze');
      case 'trickster':
        return actionLower.includes('trick') || actionLower.includes('sneak') || actionLower.includes('deceive');
      case 'oracle':
        return actionLower.includes('predict') || actionLower.includes('vision') || actionLower.includes('future');
      default:
        return false;
    }
  }

  async generateNPCMessage(
    npcId: string,
    gameContext: GameSession,
    playerActions: string[]
  ): Promise<string> {
    const npc = this.npcs.get(npcId);
    if (!npc) return '';

    try {
      // Use OpenRouter to generate contextual NPC dialogue
      const prompt = `
        You are ${npc.name}, a ${npc.personality.archetype} character in a cyberpunk RPG.
        Personality: ${npc.status} disposition, ${npc.personality.aggressiveness > 0.5 ? 'aggressive' : 'calm'} nature.
        
        Recent player actions: ${playerActions.join(', ')}
        Current game context: ${JSON.stringify(gameContext.storyContext)}
        
        Generate a brief, in-character response (1-2 sentences) that reflects your personality and reacts to the recent events.
        Stay true to your ${npc.personality.archetype} archetype.
      `;

      // TODO: Implement actual OpenRouter call for NPC dialogue
      // For now, return a fallback message
      return this.getFallbackNPCMessage(npc);
    } catch (error) {
      console.error(`Error generating NPC message for ${npcId}:`, error);
      return this.getFallbackNPCMessage(npc);
    }
  }

  private getFallbackNPCMessage(npc: NPCState): string {
    const messages = {
      'jax': [
        "The net's buzzing with new intel. Stay sharp.",
        "Security protocols are tightening. We need to move.",
        "That last hack left traces. Clean up your code."
      ],
      'lyra': [
        "I sense a disturbance in the data streams.",
        "The patterns are shifting. Something's coming.",
        "Trust your instincts, but verify your connections."
      ]
    };

    const npcMessages = messages[npc.id as keyof typeof messages] || ["..."];
    return npcMessages[Math.floor(Math.random() * npcMessages.length)];
  }

  generateBayesianRandom(
    baseChance: number,
    context: GameSession,
    playerLuck?: number
  ): number {
    const luck = playerLuck || 50; // Default neutral luck
    const contextModifier = this.getContextModifier(context);
    
    // Bayesian probability calculation
    const priorProbability = baseChance / 100;
    const luckModifier = (luck - 50) / 100; // Convert to -0.5 to +0.5 range
    const quantumSeed = this.generateQuantumSeed();
    
    // Combine factors
    let finalProbability = priorProbability + luckModifier + contextModifier;
    finalProbability = Math.max(0, Math.min(1, finalProbability)); // Clamp to 0-1
    
    // Use quantum-influenced random number
    const quantumRandom = this.quantumRandom(quantumSeed);
    
    return quantumRandom < finalProbability ? 1 : 0;
  }

  private getContextModifier(context: GameSession): number {
    // Analyze game context to provide situational modifiers
    // This is a simplified version - could be much more sophisticated
    return Math.random() * 0.1 - 0.05; // Small random context modifier
  }

  private generateQuantumSeed(): number {
    // Generate a quantum-influenced seed using various entropy sources
    const now = Date.now();
    const performanceNow = performance.now();
    const randomValues = new Uint32Array(1);
    crypto.getRandomValues(randomValues);
    
    return (now + performanceNow + randomValues[0]) % 1000000;
  }

  private quantumRandom(seed: number): number {
    // Quantum-influenced random number generator
    // This is a simplified implementation - real quantum RNG would be much more complex
    const a = 1664525;
    const c = 1013904223;
    const m = 2 ** 32;
    
    return ((a * seed + c) % m) / m;
  }
}

export const multiplayerStoryService = new MultiplayerStoryService(); 